import { db } from '../lib/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  writeBatch
} from 'firebase/firestore';
import { Match, Prediction, User, UserRanking, OverallRanking, LeagueId } from '../types';
import { canUserPredictMatch } from './matchdayService';
import { hydrateMundialMatchesAsync } from './mundial/mundialKnockoutHydrationService';

const DEFAULT_LEAGUE: LeagueId = 'liga_mx';

export const quinielaService = {
  // Obtener partidos activos agrupados por fase (liga_mx incluye docs sin campo league por compatibilidad)
  async getActiveMatches(league: LeagueId = DEFAULT_LEAGUE): Promise<Record<string, Match[]>> {
    const matchesRef = collection(db, 'matches');
    const q = query(matchesRef, where('activo', '==', true));
    const querySnapshot = await getDocs(q);
    
    const allMatches = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        date: data.date?.toDate?.() ?? data.date,
        created_at: data.created_at?.toDate?.() ?? data.created_at,
        updated_at: data.updated_at?.toDate?.() ?? data.updated_at,
      };
    }) as Match[];
    
    const matches = allMatches.filter(m => 
      m.league === league || (league === 'liga_mx' && !m.league)
    );

    let displayMatches = matches;
    if (league === 'mundial_fifa' && matches.length > 0) {
      displayMatches = await hydrateMundialMatchesAsync(matches);
    }

    // Agrupar por fase
    return displayMatches.reduce((acc, match) => {
      if (!acc[match.phase]) {
        acc[match.phase] = [];
      }
      acc[match.phase].push(match);
      return acc;
    }, {} as Record<string, Match[]>);
  },

  // Obtener predicciones del usuario (liga_mx incluye docs sin campo league por compatibilidad)
  async getUserPredictions(userId: string, league: LeagueId = DEFAULT_LEAGUE): Promise<Prediction[]> {
    const predictionsRef = collection(db, 'predictions');
    const q = query(predictionsRef, where('user_id', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const allPredictions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Prediction[];
    
    return allPredictions.filter(p => 
      p.league === league || (league === 'liga_mx' && !p.league)
    );
  },

  // Guardar pronósticos nuevos (no sobrescribe los ya capturados).
  async submitPredictions(
    userId: string,
    predictions: Record<string, { score_a: number; score_b: number }>,
    league: LeagueId = DEFAULT_LEAGUE,
    matchesById?: Record<string, Match>,
    existingMatchIds?: Set<string>
  ): Promise<number> {
    const existing = existingMatchIds ?? new Set(
      (await this.getUserPredictions(userId, league)).map((p) => p.match_id)
    );

    const batch = writeBatch(db);
    let saved = 0;
    const now = new Date();

    for (const [matchId, scores] of Object.entries(predictions)) {
      if (existing.has(matchId)) continue;

      const match = matchesById?.[matchId];
      if (match && !canUserPredictMatch(userId, match, now)) continue;

      const predictionRef = doc(collection(db, 'predictions'));
      batch.set(predictionRef, {
        user_id: userId,
        match_id: matchId,
        league,
        predicted_score_a: scores.score_a,
        predicted_score_b: scores.score_b,
        activo: true,
        created_at: now,
        updated_at: now,
      });
      saved += 1;
    }

    if (saved > 0) {
      await batch.commit();
    }

    return saved;
  },

  // Calcular ranking
  async calculateRanking(phase?: string, league: LeagueId = DEFAULT_LEAGUE): Promise<{ rankings: UserRanking[]; overallRankings: OverallRanking[] }> {
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    const users = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as User[];

    // Obtener todos los partidos y predicciones (filtrar por liga en memoria para compatibilidad)
    const matchesRef = collection(db, 'matches');
    const predictionsRef = collection(db, 'predictions');
    const [matchesSnapshot, predictionsSnapshot] = await Promise.all([
      getDocs(matchesRef),
      getDocs(predictionsRef)
    ]);

    const allMatches = matchesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Match[];
    const matches = allMatches.filter(m => m.league === league || (league === 'liga_mx' && !m.league));

    const allPredictions = predictionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Prediction[];
    const predictions = allPredictions.filter(p => p.league === league || (league === 'liga_mx' && !p.league));

    // Calcular puntos por usuario
    const userPoints: Record<string, number> = {};
    const userPhasePoints: Record<string, Record<string, number>> = {};

    // Inicializar puntos
    users.forEach(user => {
      userPoints[user.id] = 0;
      userPhasePoints[user.id] = {};
    });

    // Calcular puntos por predicción
    predictions.forEach(prediction => {
      const match = matches.find(m => m.id === prediction.match_id);
      if (!match || match.score_a === null || match.score_b === null) return;

      let points = 0;
      if (prediction.predicted_score_a === match.score_a && 
          prediction.predicted_score_b === match.score_b) {
        points = 3; // Resultado exacto
      } else if ((prediction.predicted_score_a > prediction.predicted_score_b && match.score_a > match.score_b) ||
                 (prediction.predicted_score_a < prediction.predicted_score_b && match.score_a < match.score_b) ||
                 (prediction.predicted_score_a === prediction.predicted_score_b && match.score_a === match.score_b)) {
        points = 1; // Resultado correcto
      }

      userPoints[prediction.user_id] += points;
      
      if (!userPhasePoints[prediction.user_id][match.phase]) {
        userPhasePoints[prediction.user_id][match.phase] = 0;
      }
      userPhasePoints[prediction.user_id][match.phase] += points;
    });

    // Generar rankings
    const rankings: UserRanking[] = [];
    const overallRankings: OverallRanking[] = [];

    // Rankings por fase si se especifica una
    if (phase) {
      users.forEach(user => {
        if (userPhasePoints[user.id][phase]) {
          rankings.push({
            name: user.displayName || 'Usuario desconocido',
            points: userPhasePoints[user.id][phase]
          });
        }
      });
    }

    // Ranking general
    users.forEach(user => {
      overallRankings.push({
        name: user.displayName || 'Usuario desconocido',
        total_points: userPoints[user.id]
      });
    });

    // Ordenar rankings
    rankings.sort((a, b) => b.points - a.points);
    overallRankings.sort((a, b) => b.total_points - a.total_points);

    return { rankings, overallRankings };
  }
}; 